import { Fragment, useMemo } from 'react'
import { DailyTotals } from '@/types'
import { PlanData } from '@/services/analyticsService'
import { Card } from '@/components/ui'
import { formatNumber, getCurrencySymbol } from '@/lib/utils'
import type { WeeklyPlanVsFactRow } from '@/hooks/useWeeklyPlanVsFact'

/**
 * Классическая (прежняя) таблица метрик периода: строки — дни, колонки — метрики.
 * Остаётся версией по умолчанию для всех городов, кроме пилотного (см.
 * DailyPeriodMetricsTable.tsx — там развилка).
 */
export interface DailyPeriodMetricsTableClassicProps {
  dailyData: DailyTotals[]
  planData: PlanData | null
  weeklyPlans: WeeklyPlanVsFactRow[]
  missingPlanStudios?: string[]
  zeroRevenueStudios?: string[]
  zeroPlanRevenueStudios?: string[]
  hideDelta?: boolean
  hideDeltaStudioCount?: number
  currency?: string
}

interface MetricConfig {
  key:
    | 'leads'
    | 'appointments'
    | 'newVisitors'
    | 'grossRevenue'
    | 'marketing'
    | 'newSalesCount'
    | 'newSalesAmount'
    | 'renewalsCount'
    | 'renewalsAmount'
    | 'upgradesCount'
    | 'upgradesAmount'
    | 'returningCount'
    | 'returningAmount'
    | 'privateSessionsCount'
    | 'privateSessionsAmount'
    | 'newSubscriptionsCount'
    | 'newSubscriptionsAmount'
    | 'subscriptionRenewalsCount'
    | 'subscriptionRenewalsAmount'
    | 'subscriptionUpgradesCount'
    | 'subscriptionUpgradesAmount'
    | 'subscriptionReturningCount'
    | 'subscriptionReturningAmount'
    | 'refundsAmount'
  label: string
  format: 'count' | 'currency'
}

interface ColumnGroup {
  id: string
  label: string
  keys: MetricConfig['key'][]
  hasSeparatorAfter: boolean
}

const COMMON_METRICS: MetricConfig[] = [
  { key: 'leads', label: 'Заявки', format: 'count' },
  { key: 'appointments', label: 'Записи', format: 'count' },
  { key: 'newVisitors', label: 'Приходы', format: 'count' }
]

const REGULAR_METRICS: MetricConfig[] = [
  { key: 'newSalesCount', label: 'Продажи абонементов новым / доплаты', format: 'count' },
  { key: 'newSalesAmount', label: 'Выручка по новым / доплатам', format: 'currency' },
  { key: 'renewalsCount', label: 'Продления (шт)', format: 'count' },
  { key: 'renewalsAmount', label: 'Продления (₽)', format: 'currency' },
  { key: 'upgradesCount', label: 'Апгрейды (шт)', format: 'count' },
  { key: 'upgradesAmount', label: 'Апгрейды (₽)', format: 'currency' },
  { key: 'returningCount', label: 'БЧС (шт)', format: 'count' },
  { key: 'returningAmount', label: 'БЧС (₽)', format: 'currency' }
]

const SUBSCRIPTION_METRICS: MetricConfig[] = [
  { key: 'newSubscriptionsCount', label: 'Новые подписки (шт)', format: 'count' },
  { key: 'newSubscriptionsAmount', label: 'Новые подписки (₽)', format: 'currency' },
  { key: 'subscriptionRenewalsCount', label: 'Автопродления подписок (шт)', format: 'count' },
  { key: 'subscriptionRenewalsAmount', label: 'Автопродления подписок (₽)', format: 'currency' },
  { key: 'subscriptionUpgradesCount', label: 'Переходы на подписку (шт)', format: 'count' },
  { key: 'subscriptionUpgradesAmount', label: 'Переходы на подписку (₽)', format: 'currency' },
  { key: 'subscriptionReturningCount', label: 'Возвраты в подписку (шт)', format: 'count' },
  { key: 'subscriptionReturningAmount', label: 'Возвраты в подписку (₽)', format: 'currency' }
]

const TRAILING_METRICS: MetricConfig[] = [
  { key: 'privateSessionsAmount', label: 'Индивидуальные занятия', format: 'currency' },
  { key: 'refundsAmount', label: 'Возвраты', format: 'currency' }
]

// Metric pairs for merged headers
const METRIC_PAIRS: Array<{ baseLabel: string; countKey: MetricConfig['key']; amountKey: MetricConfig['key'] }> = [
  { baseLabel: 'Продажи абонементов новым / доплаты', countKey: 'newSalesCount', amountKey: 'newSalesAmount' },
  { baseLabel: 'Продления', countKey: 'renewalsCount', amountKey: 'renewalsAmount' },
  { baseLabel: 'Апгрейды', countKey: 'upgradesCount', amountKey: 'upgradesAmount' },
  { baseLabel: 'БЧС', countKey: 'returningCount', amountKey: 'returningAmount' },
  { baseLabel: 'Новые подписки', countKey: 'newSubscriptionsCount', amountKey: 'newSubscriptionsAmount' },
  { baseLabel: 'Автопродления подписок', countKey: 'subscriptionRenewalsCount', amountKey: 'subscriptionRenewalsAmount' },
  { baseLabel: 'Переходы на подписку', countKey: 'subscriptionUpgradesCount', amountKey: 'subscriptionUpgradesAmount' },
  { baseLabel: 'Возвраты в подписку', countKey: 'subscriptionReturningCount', amountKey: 'subscriptionReturningAmount' },
]

function formatDateLabel(date: string): string {
  const parsed = new Date(date)
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit'
  }).format(parsed)
}

function toDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getDeltaTextClass(delta: number): string {
  if (delta > 0) return 'text-success/70'
  if (delta < 0) return 'text-error/70'
  return 'text-muted-foreground/70'
}

function formatDelta(delta: number): string {
  if (delta > 0) return `+${formatNumber(delta)}`
  if (delta < 0) return formatNumber(delta)
  return '0'
}

function formatDeltaValue(value: number, format: MetricConfig['format']): string {
  const rounded = Math.round(value)
  if (format === 'currency') {
    const sign = rounded > 0 ? '+' : ''
    return `${sign}${formatNumber(rounded)}`
  }
  return formatDelta(rounded)
}

function formatMissingPlansMessage(studioNumbers: string[]): string {
  if (studioNumbers.length === 1) {
    return `Не найден план студии №${studioNumbers[0]}`
  }
  return `Не найдены планы студий №${studioNumbers.join(', ')}`
}

function formatZeroRevenueMessage(studioNumbers: string[]): string {
  if (studioNumbers.length === 1) {
    return `Итоговая выручка студии №${studioNumbers[0]} равна 0`
  }
  return `Итоговая выручка студий №${studioNumbers.join(', ')} равна 0`
}

function formatHideDeltaMessage(studioCount: number): string {
  if (studioCount <= 1) {
    return 'Проверьте корректность плана по студии: итоговая выручка равна нулю.'
  }
  return 'Проверьте корректность планов по студиям: итоговая выручка равна нулю.'
}

function formatZeroPlanRevenueMessage(studioNumbers: string[]): string {
  if (studioNumbers.length === 1) {
    return `Проверьте корректность плана по студии №${studioNumbers[0]}: итоговая выручка в плане равна нулю.`
  }
  return `Проверьте корректность планов по студиям №${studioNumbers.join(', ')}: итоговая выручка в плане равна нулю.`
}

function getFactValue(day: DailyTotals, metric: MetricConfig['key']): number {
  if (metric === 'leads') return day.leads_count || 0
  if (metric === 'appointments') return day.appointments_count || 0
  if (metric === 'newVisitors') return day.new_visitors_count || 0
  if (metric === 'grossRevenue') return day.gross_revenue || 0
  if (metric === 'marketing') return day.marketing_expenses_amount || 0
  if (metric === 'newSalesCount') return day.new_sales_count || 0
  if (metric === 'newSalesAmount') return day.new_sales_amount || 0
  if (metric === 'renewalsCount') return day.renewals_count || 0
  if (metric === 'renewalsAmount') return day.renewals_amount || 0
  if (metric === 'upgradesCount') return day.upgrades_count || 0
  if (metric === 'upgradesAmount') return day.upgrades_amount || 0
  if (metric === 'returningCount') return day.returning_clients_count || 0
  if (metric === 'returningAmount') return day.returning_clients_amount || 0
  if (metric === 'privateSessionsCount') return day.private_sessions_count || 0
  if (metric === 'privateSessionsAmount') return day.private_sessions_amount || 0
  if (metric === 'newSubscriptionsCount') return day.new_subscriptions_count || 0
  if (metric === 'newSubscriptionsAmount') return day.new_subscriptions_amount || 0
  if (metric === 'subscriptionRenewalsCount') return day.subscription_renewals_count || 0
  if (metric === 'subscriptionRenewalsAmount') return day.subscription_renewals_amount || 0
  if (metric === 'subscriptionUpgradesCount') return day.subscription_upgrades_count || 0
  if (metric === 'subscriptionUpgradesAmount') return day.subscription_upgrades_amount || 0
  if (metric === 'subscriptionReturningCount') return day.subscription_returning_clients_count || 0
  if (metric === 'subscriptionReturningAmount') return day.subscription_returning_clients_amount || 0
  if (metric === 'refundsAmount') return day.refunds_amount || 0
  return 0
}

function getPlanValue(planData: PlanData | null, metric: MetricConfig['key'], dayCount: number): number {
  if (!planData || dayCount <= 0) return 0

  if (metric === 'leads') return (planData.leads_count_plan || 0) / dayCount
  if (metric === 'appointments') return (planData.appointments_count_plan || 0) / dayCount
  if (metric === 'newVisitors') return (planData.new_visitors_count_plan || 0) / dayCount
  if (metric === 'grossRevenue') return (planData.total_gross_revenue_plan || 0) / dayCount
  if (metric === 'marketing') return (planData.marketing_budget_plan || 0) / dayCount
  if (metric === 'newSalesCount') return (planData.new_sales_count_plan || 0) / dayCount
  if (metric === 'newSalesAmount') return (planData.new_sales_amount_plan || 0) / dayCount
  if (metric === 'renewalsAmount') return (planData.renewals_amount_plan || 0) / dayCount
  if (metric === 'upgradesAmount') return (planData.upgrades_amount_plan || 0) / dayCount
  if (metric === 'returningAmount') return (planData.returning_clients_amount_plan || 0) / dayCount
  return 0
}

function hasPlanInPlanData(planData: PlanData | null, metric: MetricConfig['key']): boolean {
  if (!planData) return false
  if (metric === 'leads') return planData.leads_count_plan !== undefined
  if (metric === 'appointments') return planData.appointments_count_plan !== undefined
  if (metric === 'newVisitors') return planData.new_visitors_count_plan !== undefined
  if (metric === 'grossRevenue') return planData.total_gross_revenue_plan !== undefined
  if (metric === 'marketing') return planData.marketing_budget_plan !== undefined
  if (metric === 'newSalesCount') return planData.new_sales_count_plan !== undefined
  if (metric === 'newSalesAmount') return planData.new_sales_amount_plan !== undefined
  if (metric === 'renewalsAmount') return planData.renewals_amount_plan !== undefined
  if (metric === 'upgradesAmount') return planData.upgrades_amount_plan !== undefined
  if (metric === 'returningAmount') return planData.returning_clients_amount_plan !== undefined
  return false
}

function formatFact(value: number): string {
  return formatNumber(Math.round(value))
}

function getWeeklyPlanField(week: WeeklyPlanVsFactRow, metric: MetricConfig['key']): number | null | undefined {
  if (metric === 'leads') return week.weekly_leads_plan
  if (metric === 'appointments') return week.weekly_appointments_plan
  if (metric === 'newVisitors') return week.weekly_new_visitors_plan
  if (metric === 'grossRevenue') return week.weekly_gross_revenue_plan
  if (metric === 'marketing') return week.weekly_marketing_budget_plan
  if (metric === 'newSalesCount') return week.weekly_new_sales_plan
  if (metric === 'newSalesAmount') return week.weekly_new_sales_amount_plan
  if (metric === 'renewalsCount') return week.weekly_renewals_count_plan
  if (metric === 'renewalsAmount') return week.weekly_renewals_amount_plan
  if (metric === 'upgradesCount') return week.weekly_upgrades_count_plan
  if (metric === 'upgradesAmount') return week.weekly_upgrades_amount_plan
  if (metric === 'returningCount') return week.weekly_returning_clients_count_plan
  if (metric === 'returningAmount') return week.weekly_returning_clients_amount_plan
  if (metric === 'privateSessionsCount') return week.weekly_private_sessions_count_plan
  if (metric === 'privateSessionsAmount') return week.weekly_private_sessions_amount_plan
  if (metric === 'newSubscriptionsCount') return week.weekly_new_subscriptions_count_plan
  if (metric === 'newSubscriptionsAmount') return week.weekly_new_subscriptions_amount_plan
  if (metric === 'subscriptionRenewalsCount') return week.weekly_subscription_renewals_count_plan
  if (metric === 'subscriptionRenewalsAmount') return week.weekly_subscription_renewals_amount_plan
  if (metric === 'subscriptionUpgradesCount') return week.weekly_subscription_upgrades_count_plan
  if (metric === 'subscriptionUpgradesAmount') return week.weekly_subscription_upgrades_amount_plan
  if (metric === 'subscriptionReturningCount') return week.weekly_subscription_returning_clients_count_plan
  return week.weekly_subscription_returning_clients_amount_plan
}

function getWeeklyPlanPerDay(week: WeeklyPlanVsFactRow, metric: MetricConfig['key']): number {
  const weekLen = week.week_len_days || 7
  const fieldValue = getWeeklyPlanField(week, metric)
  return (fieldValue || 0) / weekLen
}

function shouldInsertVisualSeparator(metricKey: MetricConfig['key']): boolean {
  return metricKey === 'newSalesAmount'
    || metricKey === 'returningAmount'
    || metricKey === 'subscriptionReturningAmount'
}

function buildColumnGroups(activeMetrics: MetricConfig[]): ColumnGroup[] {
  const activeKeys = new Set(activeMetrics.map(m => m.key))
  const processed = new Set<MetricConfig['key']>()
  const groups: ColumnGroup[] = []

  for (const metric of activeMetrics) {
    if (processed.has(metric.key)) continue

    const pairByCount = METRIC_PAIRS.find(p => p.countKey === metric.key)
    if (pairByCount) {
      if (activeKeys.has(pairByCount.amountKey)) {
        groups.push({
          id: pairByCount.countKey,
          label: pairByCount.baseLabel,
          keys: [pairByCount.countKey, pairByCount.amountKey],
          hasSeparatorAfter: shouldInsertVisualSeparator(pairByCount.amountKey)
        })
        processed.add(pairByCount.countKey)
        processed.add(pairByCount.amountKey)
      } else {
        groups.push({
          id: metric.key,
          label: pairByCount.baseLabel,
          keys: [metric.key],
          hasSeparatorAfter: false
        })
        processed.add(metric.key)
      }
      continue
    }

    const pairByAmount = METRIC_PAIRS.find(p => p.amountKey === metric.key)
    if (pairByAmount) {
      groups.push({
        id: metric.key,
        label: pairByAmount.baseLabel,
        keys: [metric.key],
        hasSeparatorAfter: shouldInsertVisualSeparator(metric.key)
      })
      processed.add(metric.key)
      continue
    }

    groups.push({
      id: metric.key,
      label: metric.label,
      keys: [metric.key],
      hasSeparatorAfter: shouldInsertVisualSeparator(metric.key)
    })
    processed.add(metric.key)
  }

  return groups
}

export function DailyPeriodMetricsTableClassic({
  dailyData,
  planData,
  weeklyPlans,
  missingPlanStudios = [],
  zeroRevenueStudios = [],
  zeroPlanRevenueStudios = [],
  hideDelta = false,
  hideDeltaStudioCount = 1,
  currency = 'RUB'
}: DailyPeriodMetricsTableClassicProps) {
  const currencySymbol = getCurrencySymbol(currency)
  const sortedData = useMemo(
    () => [...dailyData].sort((a, b) => a.metric_date.localeCompare(b.metric_date)),
    [dailyData]
  )
  const dayCount = sortedData.length
  const metrics = useMemo(
    () => [
      ...COMMON_METRICS,
      ...REGULAR_METRICS.map((metric) => ({
        ...metric,
        label: metric.label.replace(/₽/g, currencySymbol)
      })),
      ...SUBSCRIPTION_METRICS.map((metric) => ({
        ...metric,
        label: metric.label.replace(/₽/g, currencySymbol)
      })),
      ...TRAILING_METRICS.map((metric) => ({
        ...metric,
        label: metric.label.replace(/₽/g, currencySymbol)
      })),
      { key: 'grossRevenue', label: 'Выручка всего', format: 'currency' } as const
    ].filter((metric, index, all) => all.findIndex((m) => m.key === metric.key) === index),
    [currencySymbol]
  )

  if (!dayCount) {
    return null
  }

  const metricTotals = new Map<MetricConfig['key'], { fact: number; plan: number }>()
  const hasWeeklyPlanTotals = weeklyPlans.length > 0
  for (const metric of metrics) {
    const fact = sortedData.reduce((sum, day) => sum + getFactValue(day, metric.key), 0)

    let plan = 0
    if (hasWeeklyPlanTotals) {
      plan = weeklyPlans.reduce((sum, week) => sum + (getWeeklyPlanField(week, metric.key) || 0), 0)
    } else if (hasPlanInPlanData(planData, metric.key)) {
      plan = getPlanValue(planData, metric.key, dayCount) * dayCount
    }

    metricTotals.set(metric.key, { fact, plan })
  }

  const activeMetrics = metrics.filter((metric) => {
    const totals = metricTotals.get(metric.key)
    return !!totals && (Math.round(totals.fact) !== 0 || Math.round(totals.plan) !== 0)
  })

  if (!activeMetrics.length) {
    return null
  }

  const planByDate = new Map<string, Partial<Record<MetricConfig['key'], number>>>()
  const planPresenceByDate = new Map<string, Partial<Record<MetricConfig['key'], boolean>>>()
  const hasWeeklyPlan = weeklyPlans.length > 0

  if (hasWeeklyPlan) {
    for (const week of weeklyPlans) {
      const start = new Date(`${week.week_start}T00:00:00`)
      const end = new Date(`${week.week_end}T00:00:00`)

      const cursor = new Date(start)
      while (cursor <= end) {
        const dayKey = toDateKey(cursor)
        const existing = planByDate.get(dayKey) || {}
        const existingPresence = planPresenceByDate.get(dayKey) || {}

        const next: Partial<Record<MetricConfig['key'], number>> = { ...existing }
        const nextPresence: Partial<Record<MetricConfig['key'], boolean>> = { ...existingPresence }
        for (const metric of activeMetrics) {
          const rawWeekly = getWeeklyPlanField(week, metric.key)
          if (rawWeekly !== null && rawWeekly !== undefined) {
            const current = existing[metric.key] || 0
            next[metric.key] = current + getWeeklyPlanPerDay(week, metric.key)
            nextPresence[metric.key] = true
          }
        }
        planByDate.set(dayKey, next)
        planPresenceByDate.set(dayKey, nextPresence)

        cursor.setDate(cursor.getDate() + 1)
      }
    }
  }

  const cumulativeFact: Partial<Record<MetricConfig['key'], number>> = {}
  const cumulativePlan: Partial<Record<MetricConfig['key'], number>> = {}

  for (const metric of activeMetrics) {
    cumulativeFact[metric.key] = 0
    cumulativePlan[metric.key] = 0
  }

  const columnGroups = buildColumnGroups(activeMetrics)

  return (
    <Card className="overflow-hidden isolate">
      <div className="px-4 pt-4">
        {missingPlanStudios.length > 0 && (
          <p className="text-xs text-warning font-semibold mb-2">
            {formatMissingPlansMessage(missingPlanStudios)}
          </p>
        )}
        {zeroRevenueStudios.length > 0 && (
          <p className="text-xs text-warning font-semibold mb-2">
            {formatZeroRevenueMessage(zeroRevenueStudios)}
          </p>
        )}
        {zeroPlanRevenueStudios.length > 0 && (
          <p className="text-xs text-warning font-semibold mb-2">
            {formatZeroPlanRevenueMessage(zeroPlanRevenueStudios)}
          </p>
        )}
        {hideDelta && zeroPlanRevenueStudios.length === 0 && (
          <p className="text-xs text-warning font-semibold mb-2">
            {formatHideDeltaMessage(hideDeltaStudioCount)}
          </p>
        )}
        <h3 className="text-sm font-medium text-foreground/90">Данные по дням периода</h3>
        <p className="text-xs text-muted-foreground/85 mt-1">
          Накопительно: факт и отклонение от плановых значений на каждый день
        </p>
      </div>
      <div
        className="overflow-auto max-h-[calc(100vh-180px)]"
        style={{ maxHeight: 'calc(100dvh - 180px)' }}
      >
        <table className="w-full min-w-[1600px]">
          <thead className="tg-table-header border-y border-border">
            <tr>
              <th className="text-left p-3 text-[11px] font-normal text-muted-foreground/80 sticky top-0 left-0 z-30 bg-muted border-r border-border">
                День
              </th>
              {columnGroups.map((group) => (
                <Fragment key={group.id}>
                  <th
                    colSpan={group.keys.length}
                    className="text-center p-3 text-[11px] font-normal text-muted-foreground/80 sticky top-0 z-20 bg-muted"
                  >
                    {group.keys.length > 1
                      ? `${group.label} (шт\u00a0/\u00a0${currencySymbol})`
                      : group.label}
                  </th>
                  {group.hasSeparatorAfter && (
                    <th
                      aria-hidden="true"
                      className="w-2 min-w-[8px] p-0 bg-muted border-l border-r border-border sticky top-0 z-20"
                    />
                  )}
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((day) => (
              <tr key={day.metric_date} className="border-b border-border last:border-b-0">
                <td className="p-3 text-sm font-medium whitespace-nowrap sticky left-0 z-10 bg-card border-r border-border">
                  {formatDateLabel(day.metric_date)}
                </td>
                {activeMetrics.map((metric) => {
                  const fact = getFactValue(day, metric.key)
                  const fallbackPlan = getPlanValue(planData, metric.key, dayCount)
                  const dailyPlanFromWeeks = planByDate.get(day.metric_date)?.[metric.key]
                  const weeklyPlanExists = planPresenceByDate.get(day.metric_date)?.[metric.key] === true
                  const fallbackPlanExists = hasPlanInPlanData(planData, metric.key)
                  const hasPlan = hasWeeklyPlan ? weeklyPlanExists : fallbackPlanExists
                  const dailyPlan = hasWeeklyPlan
                    ? (dailyPlanFromWeeks || 0)
                    : fallbackPlan

                  cumulativeFact[metric.key] = (cumulativeFact[metric.key] || 0) + fact
                  if (hasPlan) {
                    cumulativePlan[metric.key] = (cumulativePlan[metric.key] || 0) + dailyPlan
                  }

                  const factValue = cumulativeFact[metric.key] || 0
                  const planValue = cumulativePlan[metric.key] || 0
                  const delta = factValue - planValue

                  return (
                    <Fragment key={metric.key}>
                      <td className="p-3 text-right">
                        <div className="inline-flex items-baseline justify-end gap-1.5 w-full">
                          <span className="text-sm font-semibold text-foreground">
                            {formatFact(factValue)}
                          </span>
                          {hasPlan && !hideDelta ? (
                            <span
                              className={`inline-block -translate-y-1 text-[10px] leading-none font-semibold ${getDeltaTextClass(delta)}`}
                            >
                              {formatDeltaValue(delta, metric.format)}
                            </span>
                          ) : (
                            <span className="inline-block h-3 w-12" />
                          )}
                        </div>
                      </td>
                      {shouldInsertVisualSeparator(metric.key) && (
                        <td
                          aria-hidden="true"
                          className="w-2 min-w-[8px] p-0 bg-muted/70 border-l border-r border-border"
                        />
                      )}
                    </Fragment>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
